import { useCallback, useState, type Dispatch, type SetStateAction } from 'react';
import {
  CostEntryMode,
  deriveCostPerGram,
  deriveCostPrice,
  hasPositiveWeight,
} from './productCostPricing.utils';

type CostFormSlice = {
  weight: string;
  costPrice: string;
};

export function useProductCostEntry<T extends CostFormSlice>(
  setFormData: Dispatch<SetStateAction<T>>,
) {
  const [costEntryMode, setCostEntryMode] = useState<CostEntryMode>('PER_PIECE');
  const [costPerGram, setCostPerGram] = useState('');

  const resetCostEntry = useCallback(() => {
    setCostEntryMode('PER_PIECE');
    setCostPerGram('');
  }, []);

  const initCostEntryFromPiece = useCallback((costPrice: string, weight: string) => {
    setCostEntryMode('PER_PIECE');
    setCostPerGram(deriveCostPerGram(costPrice, weight));
  }, []);

  const handleCostEntryModeChange = useCallback(
    (next: CostEntryMode) => {
      setCostEntryMode(next);
      setCostPerGram('');
      setFormData((prev) => ({ ...prev, costPrice: '' }) as T);
    },
    [setFormData],
  );

  const handleCostPriceChange = useCallback(
    (val: number | null, weight: string) => {
      const costPrice = val != null ? String(val) : '';
      setFormData((prev) => ({ ...prev, costPrice }) as T);
      setCostPerGram(deriveCostPerGram(costPrice, weight));
    },
    [setFormData],
  );

  const handleCostPerGramChange = useCallback(
    (val: number | null, weight: string) => {
      const nextGram = val != null ? String(val) : '';
      setCostPerGram(nextGram);
      setFormData(
        (prev) =>
          ({
            ...prev,
            costPrice: deriveCostPrice(nextGram, weight),
          }) as T,
      );
    },
    [setFormData],
  );

  const applyWeightToCosts = useCallback(
    (weight: string, mode: CostEntryMode, currentCostPrice: string, currentCostPerGram: string) => {
      if (mode === 'PER_PIECE') {
        setCostPerGram(deriveCostPerGram(currentCostPrice, weight));
        return { costPrice: currentCostPrice };
      }
      return { costPrice: deriveCostPrice(currentCostPerGram, weight) };
    },
    [],
  );

  const handleWeightChange = useCallback(
    (
      val: number | null,
      mode: CostEntryMode,
      currentCostPrice: string,
      currentCostPerGram: string,
    ) => {
      const weight = val != null ? String(val) : '';
      const derived = applyWeightToCosts(weight, mode, currentCostPrice, currentCostPerGram);
      setFormData(
        (prev) =>
          ({
            ...prev,
            weight,
            ...(mode === 'PER_GRAM' ? { costPrice: derived.costPrice } : {}),
          }) as T,
      );
    },
    [applyWeightToCosts, setFormData],
  );

  const isPricingStepValid = useCallback(
    (
      form: CostFormSlice & { sellingPrice: string },
      gram: string,
      mode: CostEntryMode,
    ): boolean => {
      // Empty sale price is allowed (persisted as 0 = unset / open price).
      const saleTrimmed = form.sellingPrice.trim();
      const saleParsed = saleTrimmed === '' ? 0 : parseFloat(saleTrimmed);
      const saleOk = !Number.isNaN(saleParsed) && saleParsed >= 0;
      const pieceOk =
        form.costPrice.trim() !== '' &&
        !isNaN(parseFloat(form.costPrice)) &&
        parseFloat(form.costPrice) > 0;

      if (mode === 'PER_GRAM') {
        const gramOk = gram.trim() !== '' && !isNaN(parseFloat(gram)) && parseFloat(gram) > 0;
        return hasPositiveWeight(form.weight) && gramOk && pieceOk && saleOk;
      }

      return pieceOk && saleOk;
    },
    [],
  );

  const hasWeight = useCallback((weight: string): boolean => hasPositiveWeight(weight), []);

  return {
    costEntryMode,
    costPerGram,
    resetCostEntry,
    initCostEntryFromPiece,
    handleCostEntryModeChange,
    handleCostPriceChange,
    handleCostPerGramChange,
    handleWeightChange,
    isPricingStepValid,
    hasWeight,
  };
}
